import * as React from "react";
import { useNavigate } from "react-router";
import { Button, Chip, Input, Label, usd2 } from "@moanhot/ui";
import {
  AdminProblem,
  useAdminApi,
  type AdminRunpodAccount,
  type AdminStorageZone,
} from "@/api/adminClient";

type Load =
  | { state: "loading" }
  | {
      state: "ready";
      rows: AdminRunpodAccount[];
      zones: Record<string, AdminStorageZone>;
    }
  | { state: "error"; message: string };

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }) + " UTC";
const dollars = (micros: number) => usd2(micros / 1_000_000);

const STATUS_TONE: Record<AdminRunpodAccount["status"], string> = {
  ready: "text-ok",
  provisioning: "text-ink-2",
  disabled: "text-err",
};

const ZONE_TONE: Record<AdminStorageZone["status"], string> = {
  ready: "text-ok",
  creating: "text-ink-2",
  deleting: "text-err",
  deleted: "text-ink-3",
};

function problemText(err: unknown, fallback: string): string {
  if (!(err instanceof AdminProblem)) return fallback;
  return `${err.detail}${err.requestId ? ` Request ${err.requestId}.` : ""}`;
}

function Register({ onDone }: { onDone: (message: string) => void }) {
  const api = useAdminApi();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  return (
    <form
      className="rounded-lg border border-line p-5"
      onSubmit={(e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const data = new FormData(form);
        const text = (k: string) =>
          ((data.get(k) as string | null) ?? "").trim();
        const threshold = Number(text("threshold") || "20");
        if (!Number.isFinite(threshold) || threshold < 0)
          return setProblem(
            "The alert threshold is a dollar amount, 0 or more.",
          );
        setBusy(true);
        setProblem(null);
        api
          .registerRunpodAccount(text("tenant"), {
            label: text("label"),
            api_key: text("api_key"),
            s3_profile_ref: text("s3_profile_ref"),
            float_alert_threshold_micro_usd: Math.round(threshold * 1_000_000),
          })
          .then((row) => {
            form.reset();
            setBusy(false);
            onDone(
              `Key saved for ${row.tenant_name} (fingerprint ${row.api_key_fingerprint}).`,
            );
          })
          .catch((err: unknown) => {
            if (err instanceof AdminProblem && err.status === 401)
              return void navigate("/signin", { replace: true });
            setProblem(problemText(err, "Could not reach the server."));
            setBusy(false);
          });
      }}
    >
      <h2 className="text-md font-medium">Register a RunPod key</h2>
      <p className="mt-1 max-w-[72ch] text-sm text-ink-2">
        Paste the key of the tenant's own RunPod account. It is stored encrypted
        and never shown again. Saving it also queues the tenant's own storage
        zone, which is created on its own within a minute. A new key pauses
        live runs until the provisioning script runs again with it. Run the
        script once the zone is ready.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="rp-tenant">Tenant ID</Label>
          <Input
            id="rp-tenant"
            name="tenant"
            required
            maxLength={40}
            placeholder="ten_..."
            className="font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rp-label">Label</Label>
          <Input id="rp-label" name="label" required maxLength={120} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rp-key">API key</Label>
          <Input
            id="rp-key"
            name="api_key"
            type="password"
            autoComplete="off"
            required
            minLength={16}
            maxLength={200}
            className="font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rp-s3">S3 profile</Label>
          <Input id="rp-s3" name="s3_profile_ref" required maxLength={64} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rp-threshold">Float alert under (USD)</Label>
          <Input
            id="rp-threshold"
            name="threshold"
            inputMode="decimal"
            defaultValue="20"
            className="font-mono"
          />
        </div>
      </div>
      {problem && (
        <p role="alert" className="mt-4 text-sm text-err">
          {problem}
        </p>
      )}
      <Button type="submit" variant="primary" className="mt-4" disabled={busy}>
        {busy ? "Saving" : "Save key"}
      </Button>
    </form>
  );
}

/** The tenant's own storage zone: status, the failed step, the next
 * automatic run, and Retry while it is still being created. */
function StorageZone({
  tenantId,
  zone,
  onRetried,
}: {
  tenantId: string;
  zone: AdminStorageZone | undefined;
  onRetried: (message: string) => void;
}) {
  const api = useAdminApi();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const canRetry = !zone || zone.status === "creating";

  return (
    <div className="mt-4 border-t border-line pt-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-ink-3">Storage zone</span>
        {zone ? (
          <Chip className={ZONE_TONE[zone.status]}>
            {zone.in_progress ? "working" : zone.status}
          </Chip>
        ) : (
          <Chip className="text-ink-3">not queued</Chip>
        )}
        {zone?.pull_zone_hostname && zone.status === "ready" && (
          <span className="font-mono text-xs text-ink-3">
            {zone.pull_zone_hostname}
          </span>
        )}
        {canRetry && (
          <Button
            type="button"
            size="sm"
            className="ml-auto"
            disabled={busy || zone?.in_progress}
            onClick={() => {
              setBusy(true);
              setProblem(null);
              api
                .retryStorageZone(tenantId)
                .then(() => {
                  setBusy(false);
                  onRetried("Zone activation scheduled. It runs within a minute.");
                })
                .catch((err: unknown) => {
                  if (err instanceof AdminProblem && err.status === 401)
                    return void navigate("/signin", { replace: true });
                  setProblem(problemText(err, "Could not reach the server."));
                  setBusy(false);
                });
            }}
          >
            {busy ? "Scheduling" : "Retry"}
          </Button>
        )}
      </div>
      {zone?.last_error && zone.status !== "ready" && (
        <p className="mt-2 font-mono text-xs text-err">{zone.last_error}</p>
      )}
      {zone && zone.status === "creating" && (
        <p className="mt-1 text-xs text-ink-3">
          {zone.attempts === 0
            ? "Waiting for its first run."
            : `${zone.attempts} failed ${zone.attempts === 1 ? "run" : "runs"}.`}{" "}
          {zone.next_attempt_at
            ? `Next run ${when(zone.next_attempt_at)}.`
            : "No run scheduled: fix the cause, then Retry."}
        </p>
      )}
      {problem && (
        <p role="alert" className="mt-2 text-sm text-err">
          {problem}
        </p>
      )}
    </div>
  );
}

function Account({
  row,
  zone,
  onRetried,
}: {
  row: AdminRunpodAccount;
  zone: AdminStorageZone | undefined;
  onRetried: (message: string) => void;
}) {
  return (
    <li className="rounded-lg border border-line p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-md font-medium">{row.tenant_name}</h2>
        <span className="font-mono text-xs text-ink-3">{row.tenant_id}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <Chip className={STATUS_TONE[row.status]}>{row.status}</Chip>
        {row.float_low && <Chip className="text-err">Float low</Chip>}
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="text-ink-3">Float</dt>
        <dd className="font-mono tabular-nums">
          {row.float_balance_micro_usd === null
            ? "Not read yet"
            : dollars(row.float_balance_micro_usd)}
          {row.float_checked_at && (
            <span className="ml-2 font-sans text-xs text-ink-3">
              read {when(row.float_checked_at)}
            </span>
          )}
        </dd>
        <dt className="text-ink-3">Alert under</dt>
        <dd className="font-mono tabular-nums">
          {dollars(row.float_alert_threshold_micro_usd)}
        </dd>
        <dt className="text-ink-3">Label</dt>
        <dd>{row.label}</dd>
        <dt className="text-ink-3">Key fingerprint</dt>
        <dd className="font-mono text-xs">{row.api_key_fingerprint}</dd>
      </dl>
      {row.endpoints.length === 0 ? (
        <p className="mt-3 text-sm text-ink-2">
          No endpoints yet. Run the provisioning script for this tenant.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-ink-3">
              <tr className="border-b border-line">
                <th className="py-1.5 pr-4 font-normal">Lane</th>
                <th className="py-1.5 pr-4 font-normal">Endpoint</th>
                <th className="py-1.5 pr-4 font-normal">Workers</th>
              </tr>
            </thead>
            <tbody>
              {row.endpoints.map((e) => (
                <tr key={e.lane} className="border-b border-line last:border-0">
                  <td className="py-1.5 pr-4 font-mono text-xs">{e.lane}</td>
                  <td className="py-1.5 pr-4 font-mono text-xs">
                    {e.endpoint_id}
                  </td>
                  <td className="py-1.5 pr-4 font-mono text-xs tabular-nums">
                    {e.workers_min} to {e.workers_max}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <StorageZone tenantId={row.tenant_id} zone={zone} onRetried={onRetried} />
    </li>
  );
}

/** Per-tenant RunPod accounts: the float each one has left, and key registration. */
export function TenantsRunpod() {
  const api = useAdminApi();
  const navigate = useNavigate();
  const [load, setLoad] = React.useState<Load>({ state: "loading" });
  const [notice, setNotice] = React.useState<string | null>(null);
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    let live = true;
    api
      .getSession()
      .then((s) => {
        if (!s) {
          void navigate("/signin", { replace: true });
          return null;
        }
        return Promise.all([api.listRunpodAccounts(), api.listStorageZones()]);
      })
      .then((pages) => {
        if (!live || !pages) return;
        const [accounts, zones] = pages;
        setLoad({
          state: "ready",
          rows: accounts.data,
          zones: Object.fromEntries(zones.data.map((z) => [z.tenant_id, z])),
        });
      })
      .catch((err: unknown) => {
        if (!live) return;
        if (err instanceof AdminProblem && err.status === 401)
          return void navigate("/signin", { replace: true });
        setLoad({
          state: "error",
          message: problemText(err, "Could not load RunPod accounts."),
        });
      });
    return () => {
      live = false;
    };
  }, [api, navigate, tick]);

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-8 md:py-8">
      <h1 className="pb-6 text-xl font-medium tracking-title">RunPod float</h1>
      {notice && (
        <p role="status" className="mb-4 text-sm text-ink-2">
          {notice}
        </p>
      )}
      {load.state === "loading" && (
        <div
          className="h-40 animate-pulse rounded-lg border border-line bg-surface"
          aria-hidden
        />
      )}
      {load.state === "error" && (
        <p role="alert" className="text-sm text-err">
          {load.message}
        </p>
      )}
      {load.state === "ready" && load.rows.length === 0 && (
        <section className="rounded-lg border border-dashed border-line-strong px-6 py-14 text-center">
          <h2 className="text-md font-medium">No RunPod accounts yet</h2>
          <p className="mt-1 text-sm text-ink-2">
            Register a tenant's key below. Its float shows here after the first
            read.
          </p>
        </section>
      )}
      {load.state === "ready" && load.rows.length > 0 && (
        <ul className="space-y-3">
          {load.rows.map((r) => (
            <Account
              key={r.tenant_id}
              row={r}
              zone={load.zones[r.tenant_id]}
              onRetried={(message) => {
                setNotice(message);
                setTick((n) => n + 1);
              }}
            />
          ))}
        </ul>
      )}
      <div className="mt-6">
        <Register
          onDone={(message) => {
            setNotice(message);
            setTick((n) => n + 1);
          }}
        />
      </div>
    </div>
  );
}
